import { getSupabaseClient } from '@/api/supabaseClient';
import { uploadImage } from '@/api/contentService';

const PAGE_COLUMNS = '*';
const REGISTRATION_COLUMNS = '*';

function handleResult(data, error, message) {
  if (error) {
    if (String(error.message || '').includes('CPF ja inscrito neste evento')) {
      throw new Error('Este CPF já possui inscrição neste evento.');
    }
    if (String(error.message || '').includes('CPF repetido na inscricao')) {
      throw new Error('CPF repetido na inscrição. Cada participante precisa ter um CPF diferente.');
    }
    if (String(error.message || '').includes('Numero de parcelas acima do permitido')) {
      throw new Error('Número de parcelas acima do permitido para a data do evento.');
    }
    throw new Error(`${message}: ${error.message}`);
  }
  return data;
}

function normalizeSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizePagePayload(payload = {}) {
  const slug = normalizeSlug(payload.slug || payload.title);
  const startDate = payload.start_date || payload.event_date || null;
  const allowedQuestionTypes = new Set(['text', 'yes_no', 'custom_options', 'checkbox']);
  const customQuestions = (Array.isArray(payload.custom_questions) ? payload.custom_questions : [])
    .filter((question) => String(question.label || '').trim())
    .map((question, index) => ({
      id: question.id || `q_${index + 1}`,
      label: String(question.label || '').trim(),
      placeholder: question.placeholder || '',
      reason_placeholder: question.reason_placeholder || '',
      option_a_label: question.option_a_label || '',
      option_b_label: question.option_b_label || '',
      type: allowedQuestionTypes.has(question.type) ? question.type : 'text',
      required: Boolean(question.required),
    }));
  const agePriceRules = (Array.isArray(payload.age_price_rules) ? payload.age_price_rules : [])
    .map((rule) => ({
      min_age: Number.isFinite(Number(rule.min_age)) ? Number(rule.min_age) : 0,
      max_age: Number.isFinite(Number(rule.max_age)) ? Number(rule.max_age) : 0,
      price: Number.isFinite(Number(rule.price)) ? Number(rule.price) : 0,
      label: rule.label || '',
    }))
    .filter((rule) => rule.max_age >= rule.min_age);
  return {
    title: String(payload.title || '').trim(),
    slug,
    description: payload.description || null,
    event_date: startDate,
    start_date: startDate,
    end_date: payload.end_date || startDate,
    start_time: payload.start_time || null,
    end_time: payload.end_time || null,
    location: payload.location || null,
    price: payload.price === '' || payload.price == null ? null : Number(payload.price),
    max_spots: payload.max_spots === '' || payload.max_spots == null ? null : Number(payload.max_spots),
    allow_waitlist: payload.allow_waitlist ?? true,
    adult_price: payload.adult_price === '' || payload.adult_price == null ? null : Number(payload.adult_price),
    age_price_rules: agePriceRules,
    custom_questions: customQuestions,
    is_active: payload.is_active ?? true,
    allow_family: payload.allow_family ?? true,
    require_cpf: payload.require_cpf ?? true,
    require_address: payload.require_address ?? true,
    payment_instructions: payload.payment_instructions || null,
    terms_text: payload.terms_text || null,
    terms_url: payload.terms_url || null,
    authorization_term_url: payload.authorization_term_url || null,
    confirmation_message: payload.confirmation_message || null,
  };
}

function normalizeParticipant(participant = {}) {
  return {
    full_name: String(participant.full_name || '').trim(),
    cpf: participant.cpf || null,
    phone: participant.phone || null,
    email: participant.email || null,
    birth_date: participant.birth_date || null,
    gender: participant.gender || null,
    address: participant.address || null,
    relationship: participant.relationship || 'Responsavel',
    is_minor: Boolean(participant.is_minor),
    guardian_name: participant.guardian_name || null,
    guardian_phone: participant.guardian_phone || null,
    amount: Number.isFinite(Number(participant.amount)) ? Number(participant.amount) : 0,
  };
}

function getAllowedPaymentInstallments(page, referenceDate = new Date()) {
  const eventDateValue = page?.start_date || page?.event_date;
  if (!eventDateValue) return 1;

  const eventDate = new Date(`${String(eventDateValue).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(eventDate.getTime())) return 1;

  const monthDifference = (
    (eventDate.getFullYear() - referenceDate.getFullYear()) * 12
  ) + eventDate.getMonth() - referenceDate.getMonth();

  return Math.max(monthDifference + 1, 1);
}

export async function listAdminRegistrationPages(limit = 100) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('event_registration_pages')
    .select(PAGE_COLUMNS)
    .order('created_date', { ascending: false })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar paginas de inscricao');
}

export async function listPublicRegistrationPages(limit = 50) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('event_registration_pages')
    .select(PAGE_COLUMNS)
    .eq('is_active', true)
    .order('start_date', { ascending: true, nullsFirst: false })
    .limit(limit);
  return handleResult(data, error, 'Erro ao listar inscricoes abertas');
}

export async function getRegistrationAvailability(pageId) {
  if (!pageId) return { taken_spots: 0 };
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('event_registration_spots', { target_page_id: pageId });
  if (error) return { taken_spots: 0 };
  return { taken_spots: Number(data || 0) };
}

export async function getPublicRegistrationPageBySlug(slug) {
  const normalizedSlug = normalizeSlug(slug);
  if (!normalizedSlug) return null;

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('event_registration_pages')
    .select(PAGE_COLUMNS)
    .eq('slug', normalizedSlug)
    .eq('is_active', true)
    .maybeSingle();
  return handleResult(data, error, 'Erro ao carregar pagina de inscricao');
}

export async function createRegistrationPage(payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('event_registration_pages')
    .insert(normalizePagePayload(payload))
    .select(PAGE_COLUMNS)
    .single();
  return handleResult(data, error, 'Erro ao criar pagina de inscricao');
}

export async function updateRegistrationPage(id, payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('event_registration_pages')
    .update(normalizePagePayload(payload))
    .eq('id', id)
    .select(PAGE_COLUMNS)
    .single();
  return handleResult(data, error, 'Erro ao atualizar pagina de inscricao');
}

export async function listAdminRegistrations(pageId) {
  const supabase = getSupabaseClient();
  let query = supabase
    .from('event_registrations')
    .select(REGISTRATION_COLUMNS)
    .order('created_date', { ascending: false })
    .limit(1000);

  if (pageId && pageId !== 'all') {
    query = query.eq('page_id', pageId);
  }

  const { data, error } = await query;
  return handleResult(data, error, 'Erro ao listar inscricoes');
}

export async function submitPublicRegistration(payload = {}) {
  const supabase = getSupabaseClient();
  const participants = (payload.participants || []).map(normalizeParticipant);
  const primary = participants[0] || {};
  const hasMinor = participants.some((participant) => participant.is_minor);
  const totalAmount = Number.isFinite(Number(payload.total_amount)) ? Number(payload.total_amount) : 0;
  const isPaymentExempt = totalAmount <= 0;
  const { data: page, error: pageError } = await supabase
    .from('event_registration_pages')
    .select('event_date,start_date')
    .eq('id', payload.page_id)
    .maybeSingle();
  handleResult(page, pageError, 'Erro ao carregar pagina de inscricao');
  const maxPaymentInstallments = getAllowedPaymentInstallments(page);
  const paymentInstallments = Math.min(
    Math.max(Number(payload.payment_installments) || 1, 1),
    maxPaymentInstallments
  );
  let authorizationFileUrl = payload.authorization_file_url || null;

  if (payload.authorization_file) {
    authorizationFileUrl = await uploadImage(payload.authorization_file, 'registration-authorizations');
  }

  const row = {
    page_id: payload.page_id,
    registration_code: payload.registration_code,
    contact_name: primary.full_name,
    contact_phone: primary.phone,
    contact_email: primary.email,
    participants,
    answers: payload.answers || {},
    total_amount: totalAmount,
    payment_method: isPaymentExempt ? 'Isento' : payload.payment_method || null,
    payment_installments: isPaymentExempt ? 1 : paymentInstallments,
    payment_status: 'Pendente',
    registration_status: payload.is_waitlist ? 'Lista de espera' : 'Recebida',
    is_waitlist: Boolean(payload.is_waitlist),
    authorization_status: hasMinor ? 'Pendente' : 'Nao se aplica',
    authorization_delivery: hasMinor ? payload.authorization_delivery || 'Entregar em maos' : 'Nao se aplica',
    authorization_file_url: authorizationFileUrl,
    notes: payload.notes || null,
    accepted_terms: Boolean(payload.accepted_terms),
    terms_opened_at: payload.terms_opened_at || null,
    history: [
      {
        at: new Date().toISOString(),
        action: payload.is_waitlist ? 'Inscrição enviada para lista de espera' : 'Inscrição enviada',
        by: 'Inscrito',
      },
    ],
  };

  const { error } = await supabase
    .from('event_registrations')
    .insert(row);
  handleResult(null, error, 'Erro ao enviar inscricao');

  const receipts = await lookupPublicRegistrationReceipt({
    registrationCode: row.registration_code,
    phone: row.contact_phone,
  });
  return Array.isArray(receipts) && receipts[0]
    ? receipts[0]
    : { ...row, created_date: new Date().toISOString() };
}

export async function lookupPublicRegistrationReceipt({ registrationCode, phone, cpf } = {}) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('public_event_registration_receipts', {
    lookup_code: String(registrationCode || '').trim(),
    lookup_phone: String(phone || '').trim(),
    lookup_cpf: String(cpf || '').trim(),
  });
  return handleResult(data, error, 'Erro ao localizar comprovante de inscricao') || [];
}

export async function uploadPublicAuthorizationTerm({ registrationId, registrationCode, file } = {}) {
  if (!registrationId || !registrationCode) throw new Error('Inscrição não localizada.');
  if (!file) throw new Error('Selecione a imagem do termo assinado.');

  const authorizationFileUrl = await uploadImage(file, 'registration-authorizations');
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('public_upload_registration_authorization', {
    target_registration_id: registrationId,
    target_registration_code: registrationCode,
    file_url: authorizationFileUrl,
  });
  return handleResult(data, error, 'Erro ao enviar termo assinado');
}

export async function updateRegistrationStatus(id, payload = {}) {
  const allowedFields = [
    'registration_status',
    'payment_status',
    'payment_method',
    'payment_installments',
    'payment_notes',
    'discount_amount',
    'discount_reason',
    'authorization_status',
    'authorization_delivery',
    'admin_notes',
    'is_waitlist',
  ];
  const data = {};
  allowedFields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(payload, field)) {
      data[field] = payload[field] === '' ? null : payload[field];
    }
  });
  if (Object.prototype.hasOwnProperty.call(data, 'discount_amount')) {
    data.discount_amount = Math.max(0, Number(data.discount_amount) || 0);
  }

  const supabase = getSupabaseClient();
  const { data: current } = await supabase
    .from('event_registrations')
    .select('history')
    .eq('id', id)
    .maybeSingle();
  const changedFields = Object.keys(data);
  if (changedFields.length > 0) {
    data.history = [
      ...((Array.isArray(current?.history) ? current.history : [])),
      {
        at: new Date().toISOString(),
        action: `Atualização: ${changedFields.join(', ')}`,
        by: 'Administrador',
      },
    ];
  }

  const { data: updated, error } = await supabase
    .from('event_registrations')
    .update(data)
    .eq('id', id)
    .select(REGISTRATION_COLUMNS)
    .single();
  return handleResult(updated, error, 'Erro ao atualizar inscricao');
}

export async function deleteRegistration(id) {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('event_registrations')
    .delete()
    .eq('id', id);
  if (error) throw new Error(`Erro ao excluir inscricao: ${error.message}`);
}
